import React from "react";
import {
  ArrowRight,
  CreditCard,
  Minus,
  Plus,
  ShieldCheck,
  ShoppingBag,
  Tag,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getImageUrl, formatCurrency } from "../core/api.jsx";
import { useCart } from "../contexts/CartContext";
import { useToast } from "../contexts/ToastContext";

export default function Cart() {
  const { cartItems, cartSubtotal, cartSyncing, updateCartItem, removeCartItem } = useCart();
  const { notify } = useToast();
  const update = async (item, quantity) => {
    try {
      await updateCartItem(item, quantity);
    } catch (error) {
      notify(error.message, "error");
    }
  };
  const remove = async (item) => {
    try {
      await removeCartItem(item);
    } catch (error) {
      notify(error.message, "error");
    }
  };
  return (
    <main className="page-shell content-page cart-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Your bag</span>
      </div>
      <div className="page-title-row">
        <div>
          <p className="eyebrow">A good choice</p>
          <h1>Shopping Cart</h1>
        </div>
        <span>
          {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
        </span>
      </div>
      {cartSyncing && !cartItems.length ? (
        <div className="empty-state">
          <h2>Loading your bag.</h2>
        </div>
      ) : !cartItems.length ? (
        <div className="empty-state">
          <span className="empty-state__icon">
            <ShoppingBag />
          </span>
          <h2>Your bag is waiting.</h2>
          <p>Find something useful, lovely, or a little bit of both.</p>
          <Link to="/shop" className="button button--primary">
            Explore the shop <ArrowRight size={17} />
          </Link>
        </div>
      ) : (
        <div className="cart-layout">
          <section className="cart-main-card">
            <div className="cart-main-card__heading">
              <div>
                <p className="eyebrow">READY WHEN YOU ARE</p>
                <h2>Your items</h2>
              </div>
              <Link className="text-link" to="/shop">Continue shopping <ArrowRight size={15} /></Link>
            </div>
            <div className="cart-columns" aria-hidden="true">
              <span>Product</span><span>Quantity</span><span>Price</span>
            </div>
            <section className="cart-list">
              {cartItems.map((item) => (
                <article className="cart-item" key={item.key}>
                  <Link
                    className="cart-item__image"
                    to={`/product/${item.product?.slug || "item"}/${item.product?.productCode || ""}`}
                  >
                    {item.image ? (
                      <img src={getImageUrl(item.image)} alt={item.name} />
                    ) : (
                      <span>Sugam<br />Store</span>
                    )}
                  </Link>
                  <div className="cart-item__details">
                    <Link to={`/product/${item.product?.slug || "item"}/${item.product?.productCode || ""}`}>
                      <h2>{item.name}</h2>
                    </Link>
                    {(item.variantName || item.colorName) && (
                      <span className="cart-item__variant">
                        {item.variantName ? `Variant: ${item.variantName}` : "Variant"}
                        {item.colorName ? ` · ${item.colorName}` : ""}
                      </span>
                    )}
                    <span className="cart-item__unit-price">
                      {formatCurrency(item.price)} each
                      {item.compareAtPrice > item.price && <del> {formatCurrency(item.compareAtPrice)}</del>}
                    </span>
                    {item.stock < 1 && <span className="cart-item__variant">Out of stock</span>}
                    <div className="quantity-control">
                      <button
                        aria-label="Decrease quantity"
                        disabled={item.quantity <= 1}
                        onClick={() => update(item, item.quantity - 1)}
                      ><Minus size={14} /></button>
                      <span>{item.quantity}</span>
                      <button
                        aria-label="Increase quantity"
                        disabled={item.quantity >= item.stock}
                        onClick={() => update(item, item.quantity + 1)}
                      ><Plus size={14} /></button>
                    </div>
                  </div>
                  <div className="cart-item__end">
                    <strong>{formatCurrency(item.price * item.quantity)}</strong>
                    <button className="text-button" onClick={() => remove(item)}>
                      <Trash2 size={15} /><span>Remove</span>
                    </button>
                  </div>
                </article>
              ))}
            </section>
          </section>
          <aside className="cart-sidebar">
            <section className="cart-coupon-card">
              <div className="cart-side-heading">
                <span className="cart-side-icon"><Tag size={18} /></span>
                <div><h2>Coupon code</h2><p>Have a code? Apply it at checkout.</p></div>
              </div>
              <Link className="cart-coupon-link" to="/checkout">Apply your coupon <ArrowRight size={16} /></Link>
            </section>
            <section className="order-summary">
              <p className="eyebrow">YOUR ORDER</p>
              <h2>Order summary</h2>
              <div className="summary-line">
                <span>Subtotal</span><strong>{formatCurrency(cartSubtotal)}</strong>
              </div>
              <div className="summary-line">
                <span>Delivery</span><span>Calculated at checkout</span>
              </div>
              <div className="summary-total">
                <span>Estimated total</span><strong>{formatCurrency(cartSubtotal)}</strong>
              </div>
              <p className="summary-note">Taxes and delivery are calculated at checkout.</p>
              <Link className="button button--primary button--full" to="/checkout">
                Continue to checkout <ArrowRight size={17} />
              </Link>
            </section>
            <div className="cart-secure-note">
              <ShieldCheck size={19} />
              <span><strong>Secure checkout</strong><small><CreditCard size={13} /> Payment options available next</small></span>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
